// The Men's / Women's switch's gesture, shared by the Ranking, Filter and Stats Table (Ben,
// 2026-11-02; live/gender-switch.ts says why). One Undo step per flip; the pick is the module's
// `_`-state, so Undo and dashboards keep it.

import { useCallback } from "react";
import type { ActiveFilters } from "../engines/stats-filter-kit.js";
import { getModState, setModState } from "../modules/bridge.js";
import { undoApi } from "../canvas/undo.js";
import { clearChipsForFlip, pickAfterFlip, type GenderSide } from "../live/gender-switch.js";

/**
 * `stateKey` holds the pick ("men" / "women"; unset = the player's gender), `quietKey` the module's
 * League chip flag (useLeagueChip): after a flip the League chip is that gender's default again,
 * quiet. `afterFlip` resets anything else a new league needs (the season: its latest).
 */
export function useGenderSwitch(
  mod: HTMLElement,
  stateKey: string,
  quietKey: string,
  active: ActiveFilters,
  bump: () => void,
  afterFlip: () => void,
): { pick: GenderSide | undefined; flip: (shown: boolean | null, playerWomens: boolean | null) => void } {
  const pick = getModState<GenderSide>(mod, stateKey);
  const flip = useCallback(
    (shown: boolean | null, playerWomens: boolean | null) => {
      if (shown === null) return;
      undoApi().saveState();
      setModState(mod, stateKey, pickAfterFlip(shown, playerWomens));
      clearChipsForFlip(active);
      setModState(mod, quietKey, true);
      setModState(mod, `${quietKey}Cleared`, undefined);
      afterFlip();
      bump();
    },
    [mod, stateKey, quietKey, active, bump, afterFlip],
  );
  return { pick, flip };
}
